import {
  getKitchenBindToken,
  hasKitchenBindToken,
  isKitchenSetupComplete,
  setKitchenBindToken,
} from '@/utils/kitchenBinding';
import { hasValidKitchenSession } from '@/utils/kitchenSession';
import { shouldUseKitchenInstallWizard } from '@/utils/kitchenInstallWizard';
import { needsKitchenPushPrompt } from '@/utils/kitchenPushPrompt';
import { isStandalonePwa } from '@/utils/pwaStandalone';

const resolvePreSetupKitchenTarget = () => (
  needsKitchenPushPrompt()
    ? { name: 'kitchen.push-prompt' }
    : { name: 'kitchen.pin' }
);

const ensureBindInBrowserUrl = (to, next) => {
  if (isStandalonePwa()) return false;
  const token = getKitchenBindToken();
  if (!token) return false;
  if (String(to.query?.bind || '').trim() === token) return false;
  next({
    path: to.path,
    query: { ...to.query, bind: token },
    hash: to.hash,
    replace: true,
  });
  return true;
};

export const kitchenRouteGuard = (to, from, next) => {
  const fromQuery = String(to.query?.bind || '').trim();
  if (fromQuery) {
    setKitchenBindToken(fromQuery);
    if (isStandalonePwa()) {
      const { bind, ...restQuery } = to.query;
      next({
        path: to.path,
        query: restQuery,
        hash: to.hash,
        replace: true,
      });
      return;
    }
    next();
    return;
  }

  if (to.name === 'kitchen-invalid') {
    next();
    return;
  }

  if (!hasKitchenBindToken()) {
    next({ name: 'kitchen-invalid' });
    return;
  }

  if (ensureBindInBrowserUrl(to, next)) {
    return;
  }

  if (!isKitchenSetupComplete()) {
    if (!shouldUseKitchenInstallWizard()) {
      if (to.name === 'kitchen.setup') {
        next({ ...resolvePreSetupKitchenTarget(), replace: true });
        return;
      }
      if (to.name === 'kitchen.push-prompt') {
        if (!needsKitchenPushPrompt()) {
          next({ name: 'kitchen.pin', replace: true });
          return;
        }
        next();
        return;
      }
      if (to.name === 'kitchen.pin') {
        if (needsKitchenPushPrompt()) {
          next({ name: 'kitchen.push-prompt', replace: true });
          return;
        }
        if (hasValidKitchenSession()) {
          next({ name: 'kitchen.queue' });
          return;
        }
        next();
        return;
      }
      next(resolvePreSetupKitchenTarget());
      return;
    }
    if (to.name === 'kitchen.setup') {
      next();
      return;
    }
    next({ name: 'kitchen.setup' });
    return;
  }

  if (to.name === 'kitchen.setup') {
    next({ name: 'kitchen.pin', replace: true });
    return;
  }

  if (to.name === 'kitchen.pin') {
    if (hasValidKitchenSession()) {
      next({ name: 'kitchen.queue' });
      return;
    }
    next();
    return;
  }

  if (!hasValidKitchenSession()) {
    next({ name: 'kitchen.pin' });
    return;
  }

  next();
};
